'use client'
import { FC, PropsWithChildren, useState, useCallback } from 'react'
import { ErrorBannerContext } from './ErrorBannerContext'
import { ApiError } from '@/root/shared-types/api/ApiError'
import axios from 'axios'
import { DateTime } from 'luxon'

const getDateFromTimeStamp = (date: Date) =>
  DateTime.fromJSDate(date, { zone: 'UTC' }).toFormat('dd LLL yyyy HH:mm:ss') + ' UTC'

const isApiError = (error: unknown): error is ApiError => !!error && typeof error === 'object' && 'status' in error

// TODO: Add optional `errorCode` field to ErrorHandlerProps.
// Provider should build the error code prefix message internally using `errorRedCodePrefix` translation key.
// This will eliminate `buildErrorMessage` duplication in usePaymentFlow, useTicketEditState, and SelectDateAndTimePage.
// Change is backwards-compatible (errorCode is optional).
interface ErrorHandlerProps {
  error: unknown
  message: string
  time: Date
}

const ErrorBannerProvider: FC<PropsWithChildren> = ({ children }) => {
  const [errorBody, setErrorBody] = useState('')

  const errorHandler = useCallback(({ error, time, message }: ErrorHandlerProps) => {
    let statusInfo: string | undefined

    if (axios.isAxiosError(error)) {
      statusInfo = error.response?.status.toString()
    } else if (isApiError(error)) {
      statusInfo = error.status.toString()
    }

    const formattedMessage = statusInfo ? `${message}\n(${statusInfo}, ${getDateFromTimeStamp(time)})` : message
    setErrorBody(formattedMessage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const clearError = useCallback(() => {
    setErrorBody('')
  }, [])

  return (
    <ErrorBannerContext.Provider value={{ errorBody, errorHandler, clearError }}>
      {children}
    </ErrorBannerContext.Provider>
  )
}

export default ErrorBannerProvider
